import { motion, AnimatePresence } from "framer-motion"
import { useState, useEffect } from "react"
import { User, MessageCircle, Settings, Mic } from "lucide-react"

const personalities = [
  {
    name: "Professional",
    description: "Formal, reliable, and straightforward communication style focused on efficiency.",
    icon: "👔",
    color: "from-blue-500 to-indigo-600",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
  },
  {
    name: "Friendly",
    description: "Warm, approachable, and conversational tone that builds rapport quickly.",
    icon: "😊",
    color: "from-green-500 to-emerald-600",
    bgColor: "bg-green-50",
    borderColor: "border-green-200",
  },
  {
    name: "Custom",
    description: "Create your own unique personality by defining specific traits and characteristics.",
    icon: "✏️",
    color: "from-orange-500 to-amber-600",
    bgColor: "bg-orange-50",
    borderColor: "border-orange-200",
  },
]

const VoiceConfig = ({ value = {}, onChange }) => {
  const [previewText, setPreviewText] = useState('Hello, I am your AI assistant. How can I help you today?');
  const [playing, setPlaying] = useState(false);
  const [loadingVoice, setLoadingVoice] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [currentAudio, setCurrentAudio] = useState(null);

  // Cleanup audio when component unmounts
  useEffect(() => {
    return () => {
      if (currentAudio) {
        currentAudio.pause();
        setCurrentAudio(null);
      }
    };
  }, [currentAudio]);

  // Pre-defined sample texts in each language
  const sampleTexts = {
    "hi-IN": "नमस्ते, मैं आपकी AI सहायक हूं। मैं आपकी कैसे मदद कर सकती हूं?",
    "en-US": "Hello, I am your AI assistant. How can I help you today?",
    "ta-IN": "வணக்கம், நான் உங்கள் AI உதவியாளர். நான் உங்களுக்கு எப்படி உதவ முடியும்?",
    "te-IN": "నమస్కారం, నేను మీ AI సహాయకురాలును. నేను మీకు ఎలా సహాయం చేయగలను?",
    "mr-IN": "नमस्कार, मी तुमची AI सहाय्यक आहे. मी तुमची कशी मदत करू शकते?",
    "bn-IN": "হ্যালো, আমি আপনার AI সহকারী। আমি আপনাকে কীভাবে সাহায্য করতে পারি?",
    "gu-IN": "નમસ્તે, હું તમારી AI સહાયક છું. હું તમારી કેવી રીતે મદદ કરી શકું?",
    "kn-IN": "ನಮಸ್ಕಾರ, ನಾನು ನಿಮ್ಮ AI ಸಹಾಯಕ. ನಾನು ನಿಮಗೆ ಹೇಗೆ ಸಹಾಯ ಮಾಡಬಹುದು?",
    "ml-IN": "നമസ്കാരം, ഞാൻ നിങ്ങളുടെ AI സഹായി. ഞാൻ നിങ്ങളെ എങ്ങനെ സഹായിക്കാം?",
    "pa-IN": "ਸਤ ਸ੍ਰੀ ਅਕਾਲ, ਮੈਂ ਤੁਹਾਡੀ AI ਸਹਾਇਕ ਹਾਂ। ਮੈਂ ਤੁਹਾਡੀ ਕਿਵੇਂ ਮਦਦ ਕਰ ਸਕਦੀ ਹਾਂ?",
    "or-IN": "ନମସ୍କାର, ମୁଁ ଆପଣଙ୍କର AI ସହାୟକ। ମୁଁ ଆପଣଙ୍କର କିପରି ସହାୟତା କରିପାରେ?",
    "as-IN": "নমস্কাৰ, মই আপোনাৰ AI সহায়ক। মই আপোনাক কেনেকৈ সহায় কৰিব পাৰোঁ?",
    "brx-IN": "खोबोर, आंनि नोंथांनि AI साहाय्यक। आंनि नोंथांखौ बेसेबां साहाय्य खालामनो हागौ?",
    "doi-IN": "नमस्कार, मैं आपकी AI सहायक हूं। मैं आपकी कैसे मदद कर सकती हूं?",
    "ks-IN": "سلام، میں آپ کی AI معاون ہوں۔ میں آپ کی کیسے مدد کر سکتی ہوں؟",
    "sa-IN": "नमस्ते, अहं भवत्या AI सहायिका अस्मि। अहं भवतः कथं साहाय्यं करोमि?",
    "sd-IN": "سلام، مان توهان جي AI معاون آهيان۔ مان توهان جي ڪيئن مدد ڪري سگهان ٿي؟",
    "ne-IN": "नमस्ते, म तपाईंको AI सहायक हुँ। म तपाईंको कसरी सहयोग गर्न सक्छु?",
    "mni-IN": "হেলো, ঈদা খোইদনা AI সহায়ক। ঈদা খোইদনা লৌরকপা হাইবসী?"
  };

  const languages = [
    { code: "en-US", name: "English" },
    { code: "hi-IN", name: "Hindi" },
    { code: "ta-IN", name: "Tamil" },
    { code: "te-IN", name: "Telugu" },
    { code: "mr-IN", name: "Marathi" },
    { code: "bn-IN", name: "Bengali" },
    { code: "gu-IN", name: "Gujarati" },
    { code: "kn-IN", name: "Kannada" },
    { code: "ml-IN", name: "Malayalam" },
    { code: "pa-IN", name: "Punjabi" },
    { code: "or-IN", name: "Odia" },
    { code: "as-IN", name: "Assamese" },
    { code: "brx-IN", name: "Bodo" },
    { code: "doi-IN", name: "Dogri" },
    { code: "ks-IN", name: "Kashmiri" },
    { code: "sa-IN", name: "Sanskrit" },
    { code: "sd-IN", name: "Sindhi" },
    { code: "ne-IN", name: "Nepali" },
    { code: "mni-IN", name: "Manipuri" }
  ];

  const selectedLanguage = value?.language || "English";
  const selectedLanguageCode = languages.find(lang => lang.name === selectedLanguage)?.code || "en-US";

  const handleLanguageChange = (languageCode) => {
    const languageName = languages.find(lang => lang.code === languageCode)?.name || "English";
    onChange({
      ...value,
      language: languageName, // Send language name instead of code
      name: "shweta",
      speed: value.speed || 1,
      pitch: value.pitch || 0
    });
    
    // Update preview text to sample text for new language
    setPreviewText(sampleTexts[languageCode] || sampleTexts["en-US"]);
    setShowLanguageDropdown(false);
  };

  const handlePreviewTextChange = (e) => {
    setPreviewText(e.target.value);
  };

  const handlePlaySample = async () => {
    if (loadingVoice) return;
    
    if (playing && currentAudio) {
      // Pause the audio
      currentAudio.pause();
      setPlaying(false);
      setCurrentAudio(null);
      return;
    }
    
    setLoadingVoice(true);
    
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8787';
      const response = await fetch(`${API_BASE_URL}/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: previewText,
          voice: "shweta"
        })
      });

      if (response.ok) {
        const audioBlob = await response.blob();
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);
        
        setCurrentAudio(audio);
        setPlaying(true);
        await audio.play();
        
        audio.onended = () => {
          setPlaying(false);
          setCurrentAudio(null);
          URL.revokeObjectURL(audioUrl);
        };
        
        audio.onerror = () => {
          setPlaying(false);
          setCurrentAudio(null);
          URL.revokeObjectURL(audioUrl);
        };
      } else {
        throw new Error('Failed to generate speech');
      }
    } catch (error) {
      console.error('Error playing voice:', error);
    } finally {
      setLoadingVoice(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Language and Voice Selection in one row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Default Language Display */}
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Default Language</h3>
          <div className="relative">
            <div 
              onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
              className="w-full p-4 border-2 border-gray-200 rounded-xl bg-white/80 backdrop-blur-sm hover:border-gray-300 hover:shadow-md transition-all duration-200 cursor-pointer flex items-center justify-between"
            >
              <span className="text-base text-gray-900">{selectedLanguage}</span>
              <div className="w-4 h-4 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                <svg className={`w-2.5 h-2.5 text-white transition-transform duration-200 ${showLanguageDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            
            {/* Modern Dropdown */}
            <AnimatePresence>
              {showLanguageDropdown && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-xl border border-gray-200 z-10 max-h-48 overflow-y-auto"
                >
                  {languages.map((language) => (
                    <div
                      key={language.code}
                      onClick={() => handleLanguageChange(language.code)}
                      className={`px-3 py-2 cursor-pointer transition-colors duration-150 hover:bg-blue-50 ${
                        selectedLanguage === language.name ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-700'
                      }`}
                    >
                      {language.name}
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Voice Selection with Test Button */}
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Voice Selection</h3>
          <div className="flex items-center gap-3">
            <div className="flex-1 p-2 border-2 border-blue-500 bg-blue-50 rounded-xl shadow-lg">
              <div className="flex items-center">
                <Mic className="w-4 h-4 mr-2 text-gray-600" />
                <span className="font-semibold text-base">Shweta</span>
              </div>
              <p className="text-xs text-gray-600 mt-1 ml-6">Clear and natural voice</p>
            </div>
            
                         {/* Play/Pause Button */}
             <motion.button
               whileHover={{ scale: 1.05, y: -2 }}
               whileTap={{ scale: 0.95 }}
               onClick={handlePlaySample}
               disabled={loadingVoice}
               className={`w-12 h-12 rounded-full font-semibold transition-all duration-200 text-sm shadow-lg hover:shadow-xl transform flex items-center justify-center ${
                 loadingVoice
                   ? "bg-orange-500 text-white cursor-not-allowed"
                   : playing
                   ? "bg-red-500 text-white"
                   : "bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600"
               }`}
             >
               {loadingVoice ? (
                 <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
               ) : playing ? (
                 <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                   <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                 </svg>
               ) : (
                 <svg className="w-10 h-7" fill="currentColor" viewBox="0 0 20 20">
                   <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                 </svg>
               )}
             </motion.button>
          </div>
        </div>
      </div>

      {/* Preview Text */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Preview Text</h3>
        
        <textarea
          className="w-full p-3 border-2 border-gray-200 rounded-xl bg-white/50 focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 text-base transition-all duration-200 resize-none"
          rows={2}
          value={previewText}
          onChange={handlePreviewTextChange}
          placeholder="Enter text to preview the voice..."
        />
      </div>
    </div>
  )
}

const CustomPersonalityForm = ({ personality, setPersonality }) => {
  const handleCustomChange = (e) => {
    const { name, value } = e.target
    setPersonality((prev) => ({ 
      ...prev, 
      customPersonality: { 
        ...prev.customPersonality, 
        [name]: value 
      } 
    }))
  }

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      className="mt-6 p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-200"
    >
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Custom Personality Traits</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Communication Style</label>
          <input
            type="text"
            name="communicationStyle"
            value={personality.customPersonality?.communicationStyle || ""}
            onChange={handleCustomChange}
            className="w-full p-3 border-2 border-gray-200 rounded-xl bg-white/50 focus:ring-4 focus:ring-orange-500/20 focus:border-orange-500 text-base transition-all duration-200"
            placeholder="e.g. Formal, Casual, Technical"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Tone</label>
          <input
            type="text"
            name="tone"
            value={personality.customPersonality?.tone || ""}
            onChange={handleCustomChange}
            className="w-full p-3 border-2 border-gray-200 rounded-xl bg-white/50 focus:ring-4 focus:ring-orange-500/20 focus:border-orange-500 text-base transition-all duration-200"
            placeholder="e.g. Friendly, Professional, Humorous"
          />
        </div>
      </div>
      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Additional Characteristics</label>
        <textarea
          name="characteristics"
          value={personality.customPersonality?.characteristics || ""}
          onChange={handleCustomChange}
          className="w-full p-3 border-2 border-gray-200 rounded-xl bg-white/50 focus:ring-4 focus:ring-orange-500/20 focus:border-orange-500 text-base transition-all duration-200 resize-none"
          rows="3"
          placeholder="Describe any additional personality traits,responses length, capabilities or characteristics..."
        />
    </div>
    </motion.div>
  )
}

const PersonalityConfig = ({ personality = {}, setPersonality, agentId, onSave, onBack }) => {
  const [focusedField, setFocusedField] = useState(null)

  // Initialize personality with defaults if not set
  useEffect(() => {
    if (!personality.voice) {
      setPersonality(prev => ({
        ...prev,
        gender: prev.gender || 'female',
        voice: {
          language: 'English',
          name: 'shweta',
          speed: 1,
          pitch: 0
        }
      }));
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target
    setPersonality((prev) => ({ ...prev, [name]: value }))
  }

  const handlePersonalitySelect = (personalityType) => {
    setPersonality((prev) => ({ ...prev, personalityType }))
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="w-full max-w-6xl mx-auto px-6 py-6">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 via-blue-900 to-indigo-900 bg-clip-text text-transparent my-4">
            AI Agent Personality
          </h1>
        </motion.div>

        <div className="space-y-5">
          {/* Agent Details Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white/70 backdrop-blur-sm rounded-2xl p-5 shadow-xl border border-white/20"
          >
              <div className="flex items-center mb-4">
                <User className="w-5 h-5 text-blue-600 mr-2" />
                <h2 className="text-lg font-semibold text-gray-900">Agent Details</h2>
              </div>

              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <motion.div whileFocus={{ scale: 1.02 }} className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Agent Name</label>
                  <input
                    type="text"
                    name="name"
                    value={personality.name || ""}
                    onChange={handleChange}
                    onFocus={() => setFocusedField("name")}
                    onBlur={() => setFocusedField(null)}
                      className="w-full p-2.5 border-2 border-gray-200 rounded-xl bg-white/50 focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 text-base transition-all duration-200"
                    placeholder="e.g. Professional Helper"
                  />
                    
                </motion.div>

                  <motion.div whileFocus={{ scale: 1.02 }} className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                    <div className="grid grid-cols-2 gap-2">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setPersonality(prev => ({ ...prev, gender: 'male' }))}
                        className={`p-2.5 rounded-xl border-2 transition-all duration-200 text-sm font-medium ${
                          personality.gender === 'male'
                            ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-lg'
                            : 'border-gray-200 bg-white/50 text-gray-600 hover:border-gray-300 hover:shadow-md'
                        }`}
                      >
                         Male
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setPersonality(prev => ({ ...prev, gender: 'female' }))}
                        className={`p-2.5 rounded-xl border-2 transition-all duration-200 text-sm font-medium ${
                          personality.gender === 'female'
                            ? 'border-pink-500 bg-pink-50 text-pink-700 shadow-lg'
                            : 'border-gray-200 bg-white/50 text-gray-600 hover:border-gray-300 hover:shadow-md'
                        }`}
                      >
                         Female
                      </motion.button>
                    </div>
                  </motion.div>
                </div>

                <motion.div whileFocus={{ scale: 1.02 }} className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Greeting Message</label>
                  <input
                    type="text"
                    name="greeting"
                    value={personality.greeting || ""}
                    onChange={handleChange}
                    onFocus={() => setFocusedField("greeting")}
                    onBlur={() => setFocusedField(null)}
                    className="w-full p-2.5 border-2 border-gray-200 rounded-xl bg-white/50 focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 text-base transition-all duration-200"
                    placeholder="Hello! I'm your professional assistant."
                  />
                </motion.div>

                <motion.div whileFocus={{ scale: 1.02 }} className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Agent Description</label>
                  <textarea
                    name="description"
                    value={personality.description || ""}
                    onChange={handleChange}
                    onFocus={() => setFocusedField("description")}
                    onBlur={() => setFocusedField(null)}
                    className="w-full p-2.5 border-2 border-gray-200 rounded-xl bg-white/50 focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 text-base transition-all duration-200 resize-none"
                    rows="3"
                    placeholder="A formal and helpful assistant that provides clear, concise answers..."
                  />
                </motion.div>
              </div>
          </motion.div>

          {/* Personality Selection Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 shadow-xl border border-white/20"
          >
            <div className="flex items-center mb-3">
                <MessageCircle className="w-5 h-5 text-blue-600 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Choose Personality</h2>
              </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {personalities.map((personalityOption, index) => (
                  <motion.div
                    key={personalityOption.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 * index }}
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                  className={`relative p-3 rounded-xl cursor-pointer border-2 transition-all duration-300 group ${
                      personality.personalityType === personalityOption.name
                      ? `${personalityOption.bgColor} ${personalityOption.borderColor} shadow-lg ring-2 ring-blue-500/20`
                      : "bg-white/50 border-gray-200 hover:border-gray-300 hover:shadow-md"
                    }`}
                    onClick={() => handlePersonalitySelect(personalityOption.name)}
                  >
                  <div className="flex items-start justify-between mb-2">
                      <div
                      className={`w-8 h-8 rounded-lg bg-gradient-to-r ${personalityOption.color} flex items-center justify-center text-lg shadow-md group-hover:scale-110 transition-transform duration-200`}
                      >
                        {personalityOption.icon}
                      </div>
                      <AnimatePresence>
                        {personality.personalityType === personalityOption.name && (
                          <motion.div
                            initial={{ scale: 0, rotate: -180 }}
                            animate={{ scale: 1, rotate: 0 }}
                            exit={{ scale: 0, rotate: 180 }}
                          className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center"
                          >
                          <div className="w-1.5 h-1.5 bg-white rounded-full" />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                  <h3 className="text-sm font-semibold text-gray-900 mb-1">{personalityOption.name}</h3>
                  <p className="text-xs text-gray-600 leading-relaxed">{personalityOption.description}</p>

                    <div
                    className={`absolute inset-0 rounded-xl bg-gradient-to-r ${personalityOption.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}
                    />
                  </motion.div>
                ))}
              </div>

            {/* Custom Personality Form */}
            <AnimatePresence>
              {personality.personalityType === "Custom" && (
                <CustomPersonalityForm personality={personality} setPersonality={setPersonality} />
              )}
            </AnimatePresence>
          </motion.div>

            {/* Voice Configuration Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 shadow-xl border border-white/20"
          >
            <div className="flex items-center mb-3">
                <Settings className="w-5 h-5 text-blue-600 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Voice Settings</h2>
              </div>
            <p className="text-gray-600 mb-3 text-sm">Configure how your AI agent sounds when speaking</p>

            <VoiceConfig
                value={personality.voice || {}}
                onChange={(voice) => setPersonality((prev) => ({ ...prev, voice }))}
              />
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default PersonalityConfig
